import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import dayjs from 'dayjs';
import { History, Info, Refresh } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import * as Loaders from '~/components/Loaders';
import * as actions from '~/store/actions';
import * as lib from '~/dependencies';
import { getCallFormated, formatCallDate } from '~/helpers';
import Rows from './Rows';
import {
  EmptyStateComponent,
  LoadingContainer,
  SectionHeader,
  LinearLoader,
  CircularLoader,
  GroupedVirtuoso,
  CallInfo,
  RefreshButton,
  FooterWrapper,
  FooterLoading,
  BlankComponent,
  LoadMoreBtn,
  Center,
} from './styles';

export default function CallHistory({ tab }) {
  // REFs
  const virtuoso = useRef(null);
  const isInitiated = useRef(false);

  // STATES
  const [currentOpen, setCurrentOpen] = useState(null);

  // SELECTORS
  const phoneCallLogsAll = useSelector(state => state?.call?.phoneCallLogsAll);
  const phoneCallLogsMy = useSelector(state => state?.call?.phoneCallLogsMy);
  const loading = useSelector(state => state?.call?.loading);
  const historyFilterOptions = useSelector(state => state.historyFilterOptions);
  const extension = useSelector(state => Number(state.auth?.userExtension));
  const openEnhancedSearch = useSelector(
    state => state?.call?.openEnhancedSearch
  );
  const noMoreItems = useSelector(state => state?.call?.noMoreItems);

  // CONST's
  const {
    tags,
    extensions,
    queues,
    directions,
    types,
    searchFor,
    dateRange,
    pageOffset,
  } = historyFilterOptions;

  // MEMOs
  const tagsMemo = useMemo(() => tags, [tags]);
  const extensionsMemo = useMemo(() => extensions, [extensions]);
  const queuesMemo = useMemo(() => queues, [queues]);
  const directionsMemo = useMemo(() => directions, [directions]);
  const typesMemo = useMemo(() => types, [types]);
  const searchForMemo = useMemo(() => searchFor, [searchFor]);
  const tabMemo = useMemo(() => tab, [tab]);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  const fetchCalllogs = useCallback(() => {
    dispatch(actions.fetchCalllogs(tabMemo === 0 ? extension : null));
  }, [dispatch, tabMemo, extension]);

  // EFFECTS
  useEffect(() => {
    // Always reset the filters when the user has navigated out from the history tab.
    if (!isInitiated.current) {
      dispatch(actions.resetHistoryFilterOptions());
      isInitiated.current = true;
    }
    fetchCalllogs();
  }, [
    fetchCalllogs,
    tagsMemo,
    extensionsMemo,
    queuesMemo,
    directionsMemo,
    typesMemo,
    searchForMemo,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(actions.setOpenEnhancedSearch(false));
      return () => {
        isInitiated.current = false;
      };
    };
  }, [dispatch]);

  const loadMore = useCallback(() => {
    if (!loading) {
      if (dateRange?.start && pageOffset === 1) {
        dispatch(
          actions.subtractOneWeek(
            dayjs(dateRange.start).format('YYYY-MM-DD HH:mm:ss')
          )
        );
      }

      dispatch(actions.fetchCalllogs(tab === 0 ? extension : null));
      dispatch(actions.setLastHistoryCheck());
    }
  }, [loading, dateRange, pageOffset, dispatch, tab, extension]);

  const whenStartReached = () => {
    if (!loading) {
      dispatch(actions.setPageOffset(1));
      if (!openEnhancedSearch) {
        dispatch(actions.resetHistoryFilterOptions());
      }
      dispatch(actions.fetchCalllogs(tab === 0 ? extension : null));
    }
  };

  const refresh = useCallback(() => {
    dispatch(actions.resetCalls());
    dispatch(actions.clearHistoryCustomFilters());
    dispatch(actions.setNoMoreItems(false));
    dispatch(actions.fetchCalllogs(tab === 0 ? extension : null));
  }, [dispatch, tab, extension]);

  const calls = tab === 1 ? phoneCallLogsAll : phoneCallLogsMy;
  const allCalls = calls.reduce((prev, next) => prev.concat(next.data), []);
  const refactData = allCalls.map(values => ({
    ...getCallFormated(values, extension, tab === 1),
    dataGroupName: formatCallDate(values.callDate, 'DD MMM YYYY'),
  }));
  const listGrouped = _(refactData)
    .groupBy(o => o.dataGroupName)
    .value();
  const groupCounts = Object.values(listGrouped).map(users => users.length);
  const groups = Object.keys(listGrouped);

  /**
   * `components.Footer` replaces the `footer` render prop of react-virtuoso 0.x.
   * It has to keep a stable identity, otherwise the library remounts the whole
   * footer subtree on every render.
   */
  const renderFooter = useCallback(
    () => (
      <>
        {loading && calls.length > 0 && !noMoreItems ? (
          <FooterLoading>
            <CircularLoader size={30} thickness={2} />
          </FooterLoading>
        ) : (
          <Center>
            {!noMoreItems ? (
              <LoadMoreBtn onClick={loadMore}>
                <lib.Trans i18nKey="components.callHistory.btnLoadMore" />
              </LoadMoreBtn>
            ) : null}
          </Center>
        )}
        {!loading && calls.length > 0 && noMoreItems ? (
          <FooterWrapper>
            <CallInfo>
              <Info />
              <span>
                <lib.Trans i18nKey="components.callHistory.noRecentActivitySubTitle" />
              </span>
            </CallInfo>
            {allCalls.length < 12 && !openEnhancedSearch ? (
              <RefreshButton type="button" onClick={refresh}>
                <Refresh color="inherit" />
              </RefreshButton>
            ) : null}
          </FooterWrapper>
        ) : null}
        {/* This is a workaround to the GroupedVirtuoso call the load more function 
          when it can't calculate the list size correctly. */}
        <BlankComponent />
      </>
    ),
    [
      loading,
      calls.length,
      allCalls.length,
      noMoreItems,
      openEnhancedSearch,
      loadMore,
      refresh,
    ]
  );

  return (
    <>
      {!loading && calls.length === 0 ? (
        <EmptyStateComponent>
          <History />
          <Typography>
            {t('components.callHistory.noRecentActivity')}
          </Typography>
          <Typography className="subtitle">
            <lib.Trans i18nKey="components.callHistory.noRecentActivitySubTitle" />
          </Typography>
        </EmptyStateComponent>
      ) : null}
      {loading && calls.length === 0 ? (
        <LoadingContainer>
          <Loaders.List numRows={10} />
        </LoadingContainer>
      ) : null}
      {loading && calls.length > 0 ? <LinearLoader /> : null}
      {calls && calls.length > 0 && (
        <GroupedVirtuoso
          ref={virtuoso}
          groupCounts={groupCounts}
          overscan={100}
          groupContent={index => (
            <SectionHeader>
              <div className="line" />
              <span>{groups[index]}</span>
              <div className="line" />
            </SectionHeader>
          )}
          itemContent={index => (
            <>
              {refactData[index]?.callId && (
                <div key={refactData[index]?.callId}>
                  <Rows
                    item={refactData[index]}
                    currentOpen={currentOpen}
                    setCurrentOpen={setCurrentOpen}
                    tab={tab}
                  />
                </div>
              )}
            </>
          )}
          endReached={loadMore}
          startReached={whenStartReached}
          components={{ Footer: renderFooter }}
        />
      )}
    </>
  );
}

CallHistory.defaultProps = {
  tab: 0,
};

CallHistory.propTypes = {
  tab: lib.PropTypes.number,
};
